import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { StripeElementsOptions } from "@stripe/stripe-js"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import Step from "@/globalComponents/atoms/Step"
import Text from "@/globalComponents/atoms/Text"
import { cleanEmptyParams } from "@/utilities/etc"
import {
  ExpectedError,
  GatedConfirmSetupIntentError,
} from "@/utilities/expectedErrors"
import { RATES_HREF, STRIPE_PROMISE } from "@/utilities/globals"
import { setupIntentQueryOptions } from "@/utilities/hooks/queries/payment"

import PayingCashLauncher from "../../-components/PayingCash/PayingCashLauncher"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID/checkout"
)({
  validateSearch: (
    search: Record<string, unknown>
  ): { paymentStatus?: "failed" } => {
    return cleanEmptyParams({
      paymentStatus:
        (search.paymentStatus as string) === "failed" ? "failed" : undefined,
    })
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { reregister } = Route.useSearch()
  const { qrLookupID, sessionID } = Route.useParams()
  const { t } = useTranslation()

  const { data } = useSuspenseQuery(setupIntentQueryOptions())

  const options: StripeElementsOptions = {
    clientSecret: data.clientSecret,
    customerSessionClientSecret: data.customerSessionClientSecret || undefined,
    appearance: {
      variables: {
        fontFamily: "Inter",
      },
    },
    fonts: [
      {
        cssSrc:
          "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
      },
    ],
  }

  const baseUrl = window.location.origin
  const returnURL = `${baseUrl}/qr/${qrLookupID}/gated/register/${sessionID}/payment-status`

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("register.checkout.header")}
      </Heading>
      <Step step={4} of={4} />
      <Elements stripe={STRIPE_PROMISE} options={options}>
        <Card>
          <Heading className="center" level={1} as={2}>
            {t("register.checkout.subhead")}
          </Heading>
          {/* TODO: we prolly should use router.history.back(), with a fallbackcase */}
          <CheckoutForm
            sessionID={sessionID}
            returnURL={returnURL}
            goBack={() =>
              navigate({
                to: "/qr/$qrLookupID/gated/register/$sessionID/cart",
                replace: true,
                search: { reregister },
              })
            }
            goNext={(key: string) =>
              navigate({
                to: "/qr/$qrLookupID/gated/register/$sessionID/confirmation",
                search: { key },
              })
            }
          />
        </Card>
      </Elements>
      {!reregister && (
        <PayingCashLauncher
          sessionID={sessionID}
          onSuccess={() =>
            navigate({
              to: "/qr/$qrLookupID/gated/register/$sessionID/complete",
            })
          }
        />
      )}
    </>
  )
}

interface CheckoutFormProps {
  goBack: () => void
  goNext: (key: string) => void
  returnURL: string
  sessionID: string
}

function CheckoutForm(props: CheckoutFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()
  const { t } = useTranslation()

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || loading}>
        <PaymentElement
          onChange={() => setError(undefined)}
          allow-top-navigation
        />
        <Separator
          orientation="horizontal"
          style={{ marginTop: "var(--space-5)" }}
        />
        <div style={{ marginTop: "var(--space-5)" }}>
          <Heading level={4} as={3}>
            {t("register.checkout.totalDue")}
          </Heading>
          <Text level={3} style={{ marginTop: "var(--space-2)" }}>
            {t("register.checkout.totalDueAmount")}{" "}
            <a href={RATES_HREF} target="_blank">
              {t("common.seeRates")}.
            </a>
          </Text>
        </div>
        {error && (
          <FieldError>
            {error instanceof ExpectedError
              ? t(error.translationKey)
              : error.message}
          </FieldError>
        )}
        <Button
          type="submit"
          hue="secondary"
          variant="default"
          isPending={loading}
        >
          {t("common.submit")}
          <Icon name="arrowRight" slot="right" />
        </Button>
      </fieldset>
    </form>
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    if (!stripe || !elements) {
      return
    }

    const paymentElement = elements.getElement(PaymentElement)
    if (!paymentElement) {
      setError(new Error("Payment details not found. Please refresh the page."))
      setLoading(false)
      return
    }

    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: props.returnURL,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      } else {
        result.setupIntent.id
        const rsp = await api.confirmSetupIntent.POST({})
        if ("failReason" in rsp) {
          throw new GatedConfirmSetupIntentError(rsp.failReason)
        }
        props.goNext(rsp.confirmationKey)
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e)
      } else {
        setError(new Error(t("errors.common.somethingWentWrong")))
      }
    } finally {
      setLoading(false)
    }
  }
}
